import {
  OrganizationPackageResponse as OrganizationPackageDto,
  Package as PackageDto,
} from '@electricjs/core_entity-client';

import { apiSlice, TagTypes } from '@/redux/slices/apiSlice';
import {
  ChangeLicenseCountInterestArgs,
  OrganizationPackageWithEntitlements,
  Package,
  PackageDeleteArgs,
  PackageLicenseCountUpdateArgs,
  PackageUpdateArgs,
  UpgradePackageArgs,
} from '@/types/packages';
import {
  invalidatesListAndById,
  providesById,
} from './helpers/cacheTagHelpers';

export const packageApiSlice = apiSlice.injectEndpoints({
  endpoints: builder => ({
    listAllPackages: builder.query<Package[], void>({
      query: () => `/v1/packages`,
      transformResponse: (response: PackageDto[]) => {
        return response?.map(pkg => {
          return {
            id: pkg.id,
            name: pkg.name,
            sku: pkg.sku,
            displayName: pkg.display_name,
          };
        });
      },
    }),
    updatePackage: builder.mutation<void, PackageUpdateArgs>({
      query: ({ organizationId, packageId, licenseCount }) => {
        return {
          url: `/v1/organizations/${organizationId}/packages`,
          method: 'PATCH',
          body: { package_id: packageId, license_count: licenseCount },
        };
      },
      // Remove the Organization tag from the invalidation list
      // once the packages are decoupled from the organization response
      invalidatesTags: (_result, _error, args) => [
        ...invalidatesListAndById(
          TagTypes.OrganizationPackage,
          args.organizationId
        ),
        TagTypes.Organization,
      ],
    }),
    updatePackageLicenseCount: builder.mutation<
      void,
      PackageLicenseCountUpdateArgs
    >({
      query: ({ organizationId, organizationPackageId, licenseCount }) => {
        return {
          url: `/v1/organizations/${organizationId}/packages/${organizationPackageId}`,
          method: 'PATCH',
          body: { license_count: licenseCount },
        };
      },
      // Remove the Organization tag from the invalidation list
      // once the packages are decoupled from the organization response
      invalidatesTags: (_result, _error, args) => [
        ...invalidatesListAndById(
          TagTypes.OrganizationPackage,
          args.organizationId
        ),
        TagTypes.Organization,
      ],
    }),
    sendChangeLicenseCountInterestNotification: builder.mutation<
      void,
      ChangeLicenseCountInterestArgs
    >({
      query: ({ organizationId }) => ({
        url: `/v1/organizations/${organizationId}/packages/send_change_license_count_interest_email`,
        method: 'POST',
      }),
    }),
    getOrganizationPackages: builder.query<
      OrganizationPackageWithEntitlements[],
      string
    >({
      query: organizationId => `/v1/organizations/${organizationId}/packages`,
      transformResponse: (response: OrganizationPackageDto[]) => {
        return response?.map(orgPkg => {
          return {
            id: orgPkg.id,
            organizationId: orgPkg.organization_id,
            package: {
              id: orgPkg.package.id,
              name: orgPkg.package.name,
              sku: orgPkg.package.sku,
              displayName: orgPkg.package.display_name,
              entitlements: orgPkg.package.entitlements ?? [],
            },
            licenseCount: orgPkg.license_count ?? null, // null means the plan has unlimited licenses
            availableLicenseCount: orgPkg.available_license_count ?? null, // null means the plan has unlimited licenses
            // TODO: replace `(orgPkg as any)` with `orgPkg` and remove the eslint-disable
            // once @electricjs/core_entity-client is bumped to include `applications_limit`
            // on OrganizationPackageResponse.
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            applicationsLimit: (orgPkg as any).applications_limit ?? null,
          };
        });
      },
      providesTags: (_results, _error, id) =>
        providesById(TagTypes.OrganizationPackage, id),
    }),
    addPackage: builder.mutation<void, PackageUpdateArgs>({
      query: ({ organizationId, packageId, licenseCount }) => {
        return {
          url: `/v1/organizations/${organizationId}/packages`,
          method: 'POST',
          body: { package_id: packageId, license_count: licenseCount },
        };
      },
      // Remove the Organization tag from the invalidation list
      // once the packages are decoupled from the organization response
      invalidatesTags: (_result, _error, args) => [
        ...invalidatesListAndById(
          TagTypes.OrganizationPackage,
          args.organizationId
        ),
        TagTypes.Organization,
      ],
    }),
    deletePackage: builder.mutation<void, PackageDeleteArgs>({
      query: ({ organizationId, organizationPackageId }) => {
        return {
          url: `/v1/organizations/${organizationId}/packages/${organizationPackageId}`,
          method: 'DELETE',
        };
      },
      // Remove the Organization tag from the invalidation list
      // once the packages are decoupled from the organization response
      invalidatesTags: (_result, _error, args) => [
        ...invalidatesListAndById(
          TagTypes.OrganizationPackage,
          args.organizationId
        ),
        TagTypes.Organization,
      ],
    }),
    upgradePackage: builder.mutation<void, UpgradePackageArgs>({
      query: ({ organizationId, targetPackageName, licenseCount }) => ({
        url: `/v1/organizations/${organizationId}/packages/upgrade`,
        method: 'POST',
        body: {
          target_package_name: targetPackageName,
          license_count: licenseCount,
        },
      }),
      invalidatesTags: (_result, _error, args) => [
        ...invalidatesListAndById(
          TagTypes.OrganizationPackage,
          args.organizationId
        ),
        TagTypes.Organization,
      ],
    }),
  }),
});

// export auto-generated hooks
export const {
  useListAllPackagesQuery,
  useAddPackageMutation,
  useDeletePackageMutation,
  useUpdatePackageMutation,
  useUpdatePackageLicenseCountMutation,
  useSendChangeLicenseCountInterestNotificationMutation,
  useGetOrganizationPackagesQuery,
  useUpgradePackageMutation,
} = packageApiSlice;
